import { preflightStaffIngress } from "@/services/staffIngress";
import React, { useState, useEffect } from "react";
import { X, ArrowLeft } from "lucide-react";
import { HardDriveIcon } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";
import { uploadFile } from "@/components/common/FileUploadV3";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import { queryClient } from "@/ReactQuery/reactQueryClient";
import { UploadInfo } from "./types";

interface Drive {
  _id: string;
  name: string;
}

interface DriveSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when uploads are starting (before HTTP requests begin) */
  onUploadStarting?: (files: File[]) => void;
  /** A file that will not be uploaded after all: a declined staff ingress
   * check (#10718). The parent drew a chip for it on start. */
  onUploadFailed?: (file: File) => void;
  /** Called when each upload completes */
  onUploadComplete?: (upload: UploadInfo) => void;
  files: File[];
  drives: Drive[];
  canUpload: boolean;
  /** Attach the document we already hold rather than refusing the upload as a
   *  duplicate (#11242). Passed through from the selection modal so the
   *  drives-enabled path behaves like the drives-disabled one. */
  reuseDuplicateUploads?: boolean;
  /** Pick a drive, upload nothing: the caller runs its own upload with the
   *  chosen id (the Documents page's Add, through the tray — round 12). With
   *  this the modal's own upload path is not used. */
  onDriveChosen?: (driveId: string) => void;
}

/**
 * Helper function to handle file path extraction for uploads.
 * Extracts the folder segments and the file with its own name from folder
 * uploads. The segments are never joined: a folder name can contain "/".
 */
function processFileForUpload(file: File) {
  const isFolderUpload = !!(
    file.webkitRelativePath || (file as any)._customRelativePath
  );

  let folderPath: string[] = [];
  let actualFileName = file.name;

  if (isFolderUpload) {
    const relativePath =
      file.webkitRelativePath || (file as any)._customRelativePath;
    // webkitRelativePath is the browser's own "/"-joined contract.
    const pathParts: string[] = relativePath.split("/");
    actualFileName = pathParts[pathParts.length - 1];
    pathParts.pop();
    folderPath = pathParts;
  }

  const correctedFile = isFolderUpload
    ? new File([file], actualFileName, {
        type: file.type,
        lastModified: file.lastModified,
      })
    : file;

  return {
    file: correctedFile,
    folderPath,
  };
}

/**
 * A modal for selecting a drive to upload files to.
 * This component handles the drive selection UI and upload logic.
 */
const DriveSelectionModal: React.FC<DriveSelectionModalProps> = ({
  isOpen,
  onClose,
  onUploadStarting,
  onUploadComplete,
  onUploadFailed,
  files,
  drives,
  canUpload,
  reuseDuplicateUploads = false,
  onDriveChosen,
}) => {
  const [selectedDriveId, setSelectedDriveId] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const orgId = useOrgId();

  // Set initial drive selection to the first drive if available
  useEffect(() => {
    if (drives.length > 0 && !selectedDriveId) {
      setSelectedDriveId(drives[0]._id);
    }
  }, [drives, selectedDriveId]);

  const handleUploadToDrive = async () => {
    if (!selectedDriveId || !canUpload || isUploading) return;
    if (onDriveChosen) {
      onDriveChosen(selectedDriveId);
      return;
    }

    // Notify parent that uploads are starting
    // Parent is responsible for closing the modal
    onUploadStarting?.(files);

    setIsUploading(true);

    try {
      // One staff ingress check for the whole pick (#10718); a refusal
      // the staff member declined sends nothing.
      const preflight = await preflightStaffIngress(
        "document",
        files.map((file) => ({
          name: file.webkitRelativePath || file.name,
          file,
        }))
      );
      if (!preflight.proceed) {
        for (const file of files) onUploadFailed?.(file);
        return;
      }
      for (const file of files) {
        try {
          const { file: correctedFile, folderPath } =
            processFileForUpload(file);

          const result = await uploadFile({
            orgId: orgId,
            file: correctedFile,
            useFilesystem: true,
            batchSize: files.length,
            driveId: selectedDriveId,
            baseDirectoryPath: folderPath,
            // Organizations with drives reach the upload through this picker
            // rather than the modal's own direct path, so the reuse behavior
            // has to be threaded here too or chat keeps refusing duplicates
            // for exactly those organizations (#11242).
            reuseDuplicate: reuseDuplicateUploads,
            ingressBatch: preflight.batch,
          });

          // Notify parent that this file upload completed
          if (result.blob_id) {
            onUploadComplete?.({
              blobId: result.blob_id,
              taskId: result._id,
              fileName: file.name,
              duplicateOf: result.duplicate_of ?? undefined,
              duplicateFileName: result.filename,
              processed: result.processed,
              processingFailed: result.processing_failed,
            });
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
        }
      }

      // Invalidate queries to refresh the file list
      queryClient.invalidateQueries({ queryKey: ["orgFileList"] });
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors mr-2"
            disabled={isUploading}
          >
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 flex-1">
            Select Drive for Upload
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isUploading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* File List */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <p className="text-sm text-gray-600 mb-2">
            Files to upload ({files.length}):
          </p>
          <div className="max-h-24 overflow-y-auto">
            <ul className="space-y-1">
              {files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center text-sm text-gray-800"
                >
                  <span className="truncate">{file.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Drive Selection */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-sm text-gray-600 mb-3">
            Choose a destination drive:
          </p>
          <div className="space-y-2">
            {drives.map((drive) => (
              <button
                key={drive._id}
                onClick={() => setSelectedDriveId(drive._id)}
                disabled={isUploading}
                className={`w-full p-3 rounded-lg border transition-all duration-200 text-left ${
                  selectedDriveId === drive._id
                    ? "border-gray-500 bg-gray-50"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                } ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <HardDriveIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {drive.name}
                    </div>
                  </div>
                  {selectedDriveId === drive._id && (
                    <div className="w-2 h-2 bg-gray-500 rounded-full flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isUploading}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <Tooltip
            title={
              canUpload ? "" : "Your team is not permitted to upload files"
            }
            placement="top"
            arrow
          >
            <span>
              <button
                onClick={handleUploadToDrive}
                disabled={!selectedDriveId || !canUpload || isUploading}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  selectedDriveId && canUpload && !isUploading
                    ? "bg-gray-800 text-white hover:bg-gray-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isUploading ? "Uploading..." : "Upload to Drive"}
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default DriveSelectionModal;
